import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, api } from "../lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  login: async () => {},
  register: async () => {},
  saveApiKey: async () => {},
  updateProfile: async () => {},
});

function applyToken(token) {
  if (!token) return;
  try {
    localStorage.setItem("auth_token", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } catch {}
}

function clearToken() {
  try {
    localStorage.removeItem("auth_token");
    delete api.defaults.headers.common["Authorization"];
  } catch {}
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const u = await auth.me();
      setUser(u);
      setError(null);
      return u;
    } catch (e) {
      setUser(null);
      if (!e?.response) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
        setError(`Backend not reachable at ${backendUrl}. Error: ${e?.message || e}`);
        console.error("Auth error: backend not accessible at", backendUrl, e);
      } else {
        setError(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptAuth = useCallback((res) => {
    if (res?.token) applyToken(res.token);
    const { token, ...u } = res || {};
    setUser(u);
    setError(null);
    return u;
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await auth.login({ email, password });
    return acceptAuth(res);
  }, [acceptAuth]);

  const register = useCallback(async (payload) => {
    const res = await auth.register(payload);
    return acceptAuth(res);
  }, [acceptAuth]);

  const devLogin = useCallback(async () => {
    const res = await auth.devLogin();
    return acceptAuth(res);
  }, [acceptAuth]);

  const saveApiKey = useCallback(async (patch) => {
    const body = typeof patch === "string" ? { google_api_key: patch } : (patch || {});
    const u = await auth.updateMe(body);
    setUser(u);
    return u;
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const u = await auth.updateMe(patch);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {}
    clearToken();
    setUser(null);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout, login, register, devLogin, saveApiKey, updateProfile, error, acceptAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
